import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Filter } from 'lucide-react';
import { Button, Field, Input, Modal, Select, Tabs, TabsContent, TabsList, TabsTrigger, Toggle } from '@/ui/primitives';
import { PageShell } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { useBoard, useCatalogo, useCreateOportunidade } from '@/modules/crm/api';
import { CrmKanban } from '@/modules/crm/components/CrmKanban';
import { CrmDetailModal } from '@/modules/crm/components/CrmDetailModal';
import { CrmStats } from '@/modules/crm/components/CrmStats';
import { TabBadgeTarefas } from '@/modules/crm/components/TabBadgeTarefas';
import { cn } from '@/lib/utils';
import {
  FRENTES,
  FRENTE_LABEL,
  TEMPERATURAS,
  TEMPERATURA_LABEL,
  type Card as CardData,
  type Coluna,
  type Frente,
  type Temperatura,
} from '@/modules/crm/schemas';

// Search params suportados:
//   ?card=<oportunidade_id>  — abre o modal de detalhe direto ao carregar.
//   Usado por MinhasTarefasView pra abrir o card a partir da lista de tarefas.
export const Route = createFileRoute('/_authed/crm')({
  component: CrmPage,
  validateSearch: (search: Record<string, unknown>): { card?: string } => {
    const c = search.card;
    return typeof c === 'string' && c ? { card: c } : {};
  },
});

type FonteFiltro = '' | 'diagnostico' | 'manual' | 'reabertura';
const SERVICO_AMBIENTAL_FALLBACK = [
  'Cadastro / Regularização de CAR',
  'Programa de Regularização Ambiental (PRA)',
  'Desembargo ambiental',
  'Limpeza de pastagem',
  'Licença Prévia (LP)',
  'Licença de Instalação (LI)',
  'Licença de Operação (LO)',
];

function NovaTriagemAmbientalModal({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}) {
  const create = useCreateOportunidade();
  const { data: catalogoData } = useCatalogo();
  const [clienteCpf, setClienteCpf] = useState('');
  const [imovelId, setImovelId] = useState('');
  const [servico, setServico] = useState('');
  const [detalhe, setDetalhe] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setClienteCpf('');
    setImovelId('');
    setServico('');
    setDetalhe('');
    setError(null);
  }, [open]);

  const servicosAmbientais = useMemo(() => {
    const fromCatalogo = (catalogoData?.catalogo ?? [])
      .filter((item) => item.frente === 'ambiental')
      .map((item) => item.servico.trim())
      .filter(Boolean);
    return Array.from(new Set([...fromCatalogo, ...SERVICO_AMBIENTAL_FALLBACK]))
      .sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [catalogoData]);

  async function handleSubmit() {
    setError(null);
    if (!clienteCpf.trim()) {
      setError('CPF do cliente é obrigatório.');
      return;
    }
    if (!servico.trim()) {
      setError('Selecione o serviço ambiental.');
      return;
    }
    try {
      await create.mutateAsync({
        cliente_cpf: clienteCpf.trim(),
        imovel_id: imovelId.trim() ? Number(imovelId) : null,
        verificacao: 'Triagem manual',
        servico_sugerido: servico.trim(),
        frente: 'ambiental',
        detalhe: detalhe.trim() || null,
      });
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao criar triagem ambiental.');
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Nova triagem ambiental"
      description="Cria a demanda no funil comercial correto: Negócios > Ambiental."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? 'Criando…' : 'Criar triagem'}
          </Button>
        </>
      }
    >
      <Field label="CPF do cliente" htmlFor="crm-amb-cpf" required>
        <Input
          id="crm-amb-cpf"
          value={clienteCpf}
          onChange={(e) => setClienteCpf(e.target.value)}
          placeholder="000.000.000-00"
          autoFocus
        />
      </Field>
      <Field label="Serviço ambiental" htmlFor="crm-amb-servico" required>
        <Select
          id="crm-amb-servico"
          value={servico}
          onChange={(e) => setServico(e.target.value)}
        >
          <option value="">Selecione o serviço</option>
          {servicosAmbientais.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="ID do imóvel" htmlFor="crm-amb-imovel" hint="Opcional nesta etapa comercial.">
        <Input
          id="crm-amb-imovel"
          value={imovelId}
          onChange={(e) => setImovelId(e.target.value)}
          inputMode="numeric"
          placeholder="Ex.: 277"
        />
      </Field>
      <Field label="Observações iniciais" htmlFor="crm-amb-detalhe" hint="Contexto rápido para a abordagem comercial.">
        <textarea
          id="crm-amb-detalhe"
          className="rounded-md border border-border bg-elevated px-3 py-2 text-sm text-text outline-none focus:border-accent"
          rows={4}
          value={detalhe}
          onChange={(e) => setDetalhe(e.target.value)}
          placeholder="Ex.: cliente pediu orçamento para LP municipal em Vilhena."
        />
      </Field>
      {error && <p className="text-sm text-danger">{error}</p>}
    </Modal>
  );
}

function CrmPage() {
  const navigate = useNavigate();
  const { card: cardParam } = Route.useSearch();
  const [frente, setFrente] = useState<Frente | ''>('');
  const [selected, setSelected] = useState<CardData | null>(null);
  const [novaTriagemAmbientalOpen, setNovaTriagemAmbientalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [temperatura, setTemperatura] = useState<Temperatura | ''>('');
  const [fonte, setFonte] = useState<FonteFiltro>('');
  const [regiao, setRegiao] = useState('');
  const [servico, setServico] = useState('');
  const [incluirFechados, setIncluirFechados] = useState(false);

  const { data, isLoading, error } = useBoard(frente);

  // Deep-link via ?card=<id> — encontra o card no board e abre o modal.
  // Roda quando o board carrega ou quando o param muda (ex.: vindo de
  // MinhasTarefasView). Limpa o filtro de frente se o card estiver em outra
  // frente, pra não ficar "fantasma" no estado.
  useEffect(() => {
    if (!cardParam || !data) return;
    if (selected?.id === cardParam) return;
    for (const col of data.colunas) {
      const found = col.processos.find((c) => c.id === cardParam);
      if (found) {
        setSelected(found);
        return;
      }
    }
    // Card não está no board atual — pode estar em outra frente. Limpa filtro
    // pra recarregar com tudo. Se ainda não achar, fica null mesmo.
    if (frente) setFrente('');
  }, [cardParam, data, selected?.id, frente]);

  function closeModal() {
    setSelected(null);
    if (cardParam) {
      void navigate({ to: '/crm', search: {} as never, replace: true });
    }
  }

  // Listas dinâmicas derivadas do board — sem chamada extra à API.
  const regioes = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    for (const col of data.colunas) {
      for (const c of col.processos) {
        if (c.municipio && c.municipio.trim()) set.add(c.municipio.trim());
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [data]);

  const servicos = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    for (const col of data.colunas) {
      for (const c of col.processos) {
        if (c.servico && c.servico.trim()) set.add(c.servico.trim());
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [data]);

  const activeCount =
    (frente ? 1 : 0) +
    (regiao ? 1 : 0) +
    (servico ? 1 : 0) +
    (temperatura ? 1 : 0) +
    (fonte ? 1 : 0) +
    (incluirFechados ? 1 : 0);
  const filtroAtivo = activeCount > 0 || !!search.trim();

  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    function onDown(e: MouseEvent) {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFiltersOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  const filtered = useMemo(() => {
    if (!data) return null;
    const q = search.trim().toLowerCase();
    const temFiltroDeCard = !!q || !!temperatura || !!fonte;

    function matches(c: CardData): boolean {
      if (q) {
        const haystack = [
          c.cliente_nome,
          c.cliente_cpf,
          c.imovel_denominacao,
          c.imovel_matricula,
          c.verificacao,
          c.servico,
          c.municipio,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (regiao && (c.municipio ?? '').trim() !== regiao) return false;
      if (servico && (c.servico ?? '').trim() !== servico) return false;
      if (temperatura && c.temperatura !== temperatura) return false;
      if (fonte && c.fonte !== fonte) return false;
      return true;
    }

    const aplicaFiltroCard = temFiltroDeCard || !!regiao || !!servico;
    const colunas: Coluna[] = data.colunas
      .filter((col) =>
        incluirFechados
          ? true
          : col.id !== 'descartado',
      )
      .map((col) => ({
        ...col,
        processos: aplicaFiltroCard ? col.processos.filter(matches) : col.processos,
      }));

    const total = colunas.reduce((s, c) => s + c.processos.length, 0);
    return { colunas, total };
  }, [data, search, regiao, servico, temperatura, fonte, incluirFechados]);

  function limparFiltros() {
    setSearch('');
    setFrente('');
    setRegiao('');
    setServico('');
    setTemperatura('');
    setFonte('');
    setIncluirFechados(false);
  }

  return (
    <PageShell
      title={<span className="text-[18px] font-semibold leading-tight">Oportunidades</span>}
      titleSize="compact"
      headerClassName="pt-4 pb-2"
    >
      <Tabs
        value="oportunidades"
        onValueChange={(v) => {
          if (v === 'lista') void navigate({ to: '/crm/lista' });
          else if (v === 'minhas-tarefas') void navigate({ to: '/crm/minhas-tarefas' });
          else if (v === 'relatorios') void navigate({ to: '/crm/relatorios' });
        }}
      >
        {/* Abas + busca/filtro na MESMA linha: abas a esquerda, controles a
            direita. A borda inferior (sublinhado das abas) vai na linha pra
            atravessar toda a largura. */}
        <div className="flex items-end justify-between gap-4 border-b border-border">
          <TabsList className="border-b-0">
            <TabsTrigger value="oportunidades">Kanban</TabsTrigger>
            <TabsTrigger value="lista">Lista</TabsTrigger>
            <TabsTrigger value="minhas-tarefas">
              Minhas tarefas
              <TabBadgeTarefas />
            </TabsTrigger>
            <TabsTrigger value="relatorios">Relatórios</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2 pb-2">
            <div className="relative w-[280px]">
            <Search
              size={16}
              strokeWidth={1.9}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, CPF, imóvel, verificação ou serviço"
              className="h-10 w-full rounded-[10px] bg-surface2 pl-10 pr-3 text-sm text-text outline-none transition-shadow placeholder:text-text-muted focus:ring-2 focus:ring-[var(--accent-soft)]"
            />
          </div>

          <div ref={filterWrapRef} className="relative">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className={cn(
                'inline-flex h-10 items-center gap-2 rounded-[10px] px-4 text-sm transition-colors',
                activeCount > 0 || filtersOpen
                  ? 'bg-[var(--filter-active-bg)] text-[var(--filter-active-fg)]'
                  : 'bg-surface2 text-text-2 hover:text-text',
              )}
            >
              <Filter size={15} strokeWidth={1.9} />
              Filtrar
              {activeCount > 0 && (
                <span className="inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-[var(--filter-badge-bg)] px-1 text-[0.62rem] font-bold leading-none text-[var(--filter-badge-fg)]">
                  {activeCount}
                </span>
              )}
            </button>

            {filtersOpen && (
              <div
                className={cn(
                  'absolute top-[calc(100%+6px)] right-0 z-50',
                  'bg-surface border border-border rounded-xl shadow-[var(--shadow-over)]',
                  'p-4 min-w-[280px] flex flex-col gap-3',
                )}
              >
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="crm-flt-frente"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Frente
                  </label>
                  <Select
                    id="crm-flt-frente"
                    value={frente}
                    onChange={(e) => setFrente(e.target.value as Frente | '')}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todas</option>
                    {FRENTES.map((f) => (
                      <option key={f} value={f}>
                        {FRENTE_LABEL[f]}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="crm-flt-regiao"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Região
                  </label>
                  <Select
                    id="crm-flt-regiao"
                    value={regiao}
                    onChange={(e) => setRegiao(e.target.value)}
                    className="text-[0.82rem]"
                    disabled={regioes.length === 0}
                  >
                    <option value="">Todas</option>
                    {regioes.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="crm-flt-servico"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Tipo de serviço
                  </label>
                  <Select
                    id="crm-flt-servico"
                    value={servico}
                    onChange={(e) => setServico(e.target.value)}
                    className="text-[0.82rem]"
                    disabled={servicos.length === 0}
                  >
                    <option value="">Todos</option>
                    {servicos.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="crm-flt-temp"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Temperatura
                  </label>
                  <Select
                    id="crm-flt-temp"
                    value={temperatura}
                    onChange={(e) => setTemperatura(e.target.value as Temperatura | '')}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todas</option>
                    {TEMPERATURAS.map((t) => (
                      <option key={t} value={t}>
                        {TEMPERATURA_LABEL[t]}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="crm-flt-fonte"
                    className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                  >
                    Fonte
                  </label>
                  <Select
                    id="crm-flt-fonte"
                    value={fonte}
                    onChange={(e) => setFonte(e.target.value as FonteFiltro)}
                    className="text-[0.82rem]"
                  >
                    <option value="">Todas</option>
                    <option value="diagnostico">Diagnóstico</option>
                    <option value="manual">Manual</option>
                    <option value="reabertura">Reabertura</option>
                  </Select>
                </div>

                <label className="flex items-center gap-2 text-[0.82rem] text-text-2 cursor-pointer select-none">
                  <Toggle checked={incluirFechados} onCheckedChange={setIncluirFechados} />
                  Incluir descartados
                </label>

                <button
                  type="button"
                  onClick={limparFiltros}
                  disabled={!filtroAtivo}
                  className={cn(
                    'self-start text-[0.78rem] text-accent bg-transparent border-0 p-0',
                    'hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline',
                  )}
                >
                  Limpar filtros
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

        <TabsContent value="oportunidades">
      {/* PageShell já envolve children em px-8. */}
      <CrmStats />

      {isLoading && (
        <div className="py-6 text-sm text-text-muted">carregando…</div>
      )}
      {error && (
        <div className="py-6 text-sm text-danger">
          {error instanceof ApiError ? error.message : 'erro ao carregar oportunidades'}
        </div>
      )}
      {filtered && <CrmKanban colunas={filtered.colunas} onCardClick={setSelected} />}

      <CrmDetailModal card={selected} onClose={closeModal} />
      <NovaTriagemAmbientalModal
        open={novaTriagemAmbientalOpen}
        onOpenChange={setNovaTriagemAmbientalOpen}
        onCreated={() => setFrente('ambiental')}
      />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
